/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import { Search, ArrowLeftRight, PackageSearch, Truck, PackageCheck, XCircle, CheckCircle2 } from "lucide-react";
import ReusableModal from "../../ManageSettings/ReusableModal";
import { RegularButton } from "../../../../common/ReusableComponent/CustomButton";
import PaginatedSearchSelect from "../../../../common/ReusableComponent/PaginatedSearchSelect/PaginatedSearchSelect";
import useApi from "../../../../../utils/api-manager/Helper/useApi";
import StockTransferController, {
  SOURCE_TRANSITIONS,
  DESTINATION_TRANSITIONS,
  STATUS_LABELS,
} from "./StockTransferController";

const STATUS_CHIP_CLASS = {
  requested: "sxfer-chip-info",
  approved: "sxfer-chip-info",
  rejected: "sxfer-chip-danger",
  packing: "sxfer-chip-warning",
  sent_to_courier: "sxfer-chip-warning",
  sent: "sxfer-chip-warning",
  received: "sxfer-chip-success",
  cancelled: "sxfer-chip-danger",
};

// Small confirm/remarks modal shared by every status-changing action below -
// approve/reject/cancel need a remark from the actor, plain forward moves
// (packing/sent to courier/sent) don't.
const ActionModal = ({ action, onCancel, onConfirm, submitting }) => {
  const [remarks, setRemarks] = useState("");
  if (!action) return null;
  const needsRemarks = action.status === "rejected" || action.status === "cancelled";
  return (
    <ReusableModal title={action.title} show={Boolean(action)} handleClose={onCancel} width="480px">
      <div className="sxfer-action-modal">
        <p>{action.description}</p>
        <div className="sxfer-form-group">
          <label>
            Remarks{needsRemarks ? " (required)" : " (optional)"}
          </label>
          <textarea
            rows={3}
            value={remarks}
            onChange={(event) => setRemarks(event.target.value)}
            placeholder={needsRemarks ? "Let the other store know why..." : "Add a note (optional)"}
          />
        </div>
        <div className="sxfer-modal-actions">
          <RegularButton text="Cancel" onClick={onCancel} backgroundColor="#ffffff" color="#282E26" />
          <RegularButton
            text={submitting ? "Saving..." : action.confirmLabel}
            onClick={() => onConfirm(remarks)}
            backgroundColor={action.danger ? "#dc2626" : "#282e26"}
            color="#ffffff"
            disabled={submitting || (needsRemarks && !remarks.trim())}
          />
        </div>
      </div>
    </ReusableModal>
  );
};

const ReceiveModal = ({ request, onCancel, onConfirm, submitting }) => {
  // Default path: this product likely doesn't exist in the requester's own
  // catalog yet, so receiving auto-creates it (same title/category/
  // description/purchase price as the source product) - the only thing
  // worth asking about is whether they want to sell it at a different
  // price than the source store does. "Link an existing product" is the
  // alternative for when they already stock the same item under their own
  // catalog entry.
  const [mode, setMode] = useState("create"); // "create" | "link"
  const [linkedProduct, setLinkedProduct] = useState(null);
  const [sellingPrice, setSellingPrice] = useState("");
  const { GetPaginatedData } = useApi();

  useEffect(() => {
    if (request) {
      setMode("create");
      setLinkedProduct(null);
      setSellingPrice(request.source_selling_price != null ? String(request.source_selling_price) : "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request?.id]);

  if (!request) return null;

  return (
    <ReusableModal title="Confirm Stock Received" show={Boolean(request)} handleClose={onCancel} width="520px">
      <div className="sxfer-action-modal">
        <p>
          Confirming receipt of <strong>{request.requested_qty}</strong> pcs of{" "}
          <strong>{request.product_title}</strong> from <strong>{request.source_store_name || "the other store"}</strong>.
        </p>

        {mode === "create" ? (
          <>
            <div className="sxfer-form-group">
              <label>Selling price for your store</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={sellingPrice}
                onChange={(event) => setSellingPrice(event.target.value)}
                placeholder="e.g. 499.00"
              />
              <small className="sxfer-hint">
                This product isn't in your catalog yet, so receiving it will add it automatically - same title,
                category and description as the source store's listing. Pre-filled with the source store's selling
                price; change it if you want to sell at a different price.
              </small>
            </div>
            <button type="button" className="sxfer-linktoggle" onClick={() => setMode("link")}>
              Already have this product? Link it instead.
            </button>
          </>
        ) : (
          <div className="sxfer-form-group">
            <label>Link to an existing product in your catalog</label>
            <PaginatedSearchSelect
              api="productData"
              GetPaginatedData={GetPaginatedData}
              getOptionLabel={(product) => product?.title || ""}
              value={linkedProduct}
              onSelect={setLinkedProduct}
              placeholder="Search your products..."
            />
            <small className="sxfer-hint">
              Stock will be added to this product's existing listing and pricing instead of creating a new one.
            </small>
            <button type="button" className="sxfer-linktoggle" onClick={() => setMode("create")}>
              Don't have it yet? Add it as a new product instead.
            </button>
          </div>
        )}

        <div className="sxfer-modal-actions">
          <RegularButton text="Cancel" onClick={onCancel} backgroundColor="#ffffff" color="#282E26" />
          <RegularButton
            text={submitting ? "Saving..." : "Confirm Received"}
            onClick={() =>
              onConfirm({
                destinationProductId: mode === "link" ? linkedProduct?.id || null : null,
                sellingPrice: mode === "create" ? sellingPrice : null,
              })
            }
            backgroundColor="#282e26"
            color="#ffffff"
            disabled={submitting || (mode === "link" && !linkedProduct)}
          />
        </div>
      </div>
    </ReusableModal>
  );
};

const RequestQtyModal = ({ product, onCancel, onConfirm, submitting }) => {
  const [qty, setQty] = useState("");
  if (!product) return null;
  const qtyNumber = Number(qty);
  const invalid = !qty || Number.isNaN(qtyNumber) || qtyNumber <= 0;
  return (
    <ReusableModal title="Request Stock" show={Boolean(product)} handleClose={onCancel} width="460px">
      <div className="sxfer-action-modal">
        <p>
          Requesting <strong>{product.title}</strong>
          {product.barcode ? ` (Barcode: ${product.barcode})` : ""} from <strong>{product.storeName}</strong> -{" "}
          {product.availableStock} pcs currently available there.
        </p>
        <div className="sxfer-form-group">
          <label>Quantity to request (pcs)</label>
          <input
            type="number"
            min="1"
            value={qty}
            onChange={(event) => setQty(event.target.value)}
            placeholder="e.g. 50"
          />
          {qtyNumber > product.availableStock && qty && (
            <small className="sxfer-hint sxfer-hint-warning">
              This is more than the {product.availableStock} pcs currently shown as available - the other store may
              not be able to fulfil the full amount.
            </small>
          )}
        </div>
        <div className="sxfer-modal-actions">
          <RegularButton text="Cancel" onClick={onCancel} backgroundColor="#ffffff" color="#282E26" />
          <RegularButton
            text={submitting ? "Sending..." : "Send Request"}
            onClick={() => onConfirm(qtyNumber)}
            backgroundColor="#282e26"
            color="#ffffff"
            disabled={invalid || submitting}
          />
        </div>
      </div>
    </ReusableModal>
  );
};

const StockTransfer = () => {
  const {
    myStoreId,
    otherStores,
    browseSearch,
    setBrowseSearch,
    browseLoading,
    browseGroups,
    searchAllStores,
    requestDirection,
    setRequestDirection,
    requests,
    requestsLoading,
    fetchRequests,
    createTransferRequest,
    updateTransferStatus,
  } = StockTransferController();

  const [activeTab, setActiveTab] = useState("find");
  const [requestProduct, setRequestProduct] = useState(null);
  const [pendingAction, setPendingAction] = useState(null); // { transferId, status, title, description, confirmLabel, danger }
  const [receiveTarget, setReceiveTarget] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (activeTab === "find" && otherStores.length > 0) {
      searchAllStores(browseSearch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, otherStores.length]);

  useEffect(() => {
    if (activeTab !== "find" || otherStores.length === 0) return undefined;
    const timeout = setTimeout(() => searchAllStores(browseSearch), 350);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [browseSearch]);

  useEffect(() => {
    if (activeTab === "requests") {
      fetchRequests(requestDirection);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, requestDirection]);

  const handleSendRequest = async (qty) => {
    setSubmitting(true);
    try {
      await createTransferRequest({
        sourceStore: requestProduct.storeId,
        sourceStoreName: requestProduct.storeName || "",
        sourceProductId: requestProduct.productId,
        productTitle: requestProduct.title,
        productBarcode: requestProduct.barcode,
        uomTitle: requestProduct.uomTitle,
        requestedQty: qty,
        sourceSellingPrice: requestProduct.sellingPrice,
        sourcePurchasePrice: requestProduct.purchasePrice,
      });
      toast.success("Transfer request sent.");
      setRequestProduct(null);
      setActiveTab("requests");
      setRequestDirection("outgoing");
    } catch (error) {
      console.error("Failed to send transfer request:", error);
      toast.error(error?.response?.data?.error || "Failed to send transfer request.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmAction = async (remarks) => {
    setSubmitting(true);
    try {
      await updateTransferStatus(pendingAction.transferId, {
        status: pendingAction.status,
        remarks: remarks || undefined,
      });
      toast.success(`Request marked as "${STATUS_LABELS[pendingAction.status]}".`);
      setPendingAction(null);
      fetchRequests(requestDirection);
    } catch (error) {
      console.error("Failed to update transfer request:", error);
      toast.error(error?.response?.data?.error || "Failed to update the request.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmReceive = async ({ destinationProductId, sellingPrice }) => {
    setSubmitting(true);
    try {
      await updateTransferStatus(receiveTarget.id, {
        status: "received",
        destinationProduct: destinationProductId,
        sellingPrice: destinationProductId ? null : sellingPrice,
      });
      toast.success(
        destinationProductId
          ? "Marked as received and added to that product's stock."
          : "Marked as received - added as a new product in your catalog.",
      );
      setReceiveTarget(null);
      fetchRequests(requestDirection);
    } catch (error) {
      console.error("Failed to mark request received:", error);
      toast.error(error?.response?.data?.error || "Failed to mark this request as received.");
    } finally {
      setSubmitting(false);
    }
  };

  const openAction = (request, status) => {
    const copy = {
      approved: {
        title: "Approve Request",
        description: `Approve sending ${request.requested_qty} pcs of ${request.product_title} to ${request.destination_store_name || "the requesting store"}?`,
        confirmLabel: "Approve",
      },
      rejected: {
        title: "Reject Request",
        description: `Reject this request for ${request.requested_qty} pcs of ${request.product_title}?`,
        confirmLabel: "Reject",
        danger: true,
      },
      packing: {
        title: "Mark as Packing",
        description: `Mark ${request.product_title} as being packed for dispatch?`,
        confirmLabel: "Mark Packing",
      },
      sent_to_courier: {
        title: "Mark Sent to Courier",
        description: `This deducts ${request.requested_qty} pcs from your stock now. Continue?`,
        confirmLabel: "Mark Sent to Courier",
      },
      sent: {
        title: "Mark as Sent",
        description: "Confirm the courier has picked this up and it's on its way?",
        confirmLabel: "Mark Sent",
      },
      cancelled: {
        title: "Cancel Request",
        description: "Cancel this transfer request?",
        confirmLabel: "Cancel Request",
        danger: true,
      },
    }[status];
    setPendingAction({ transferId: request.id, status, ...copy });
  };

  const renderActions = (request) => {
    const isSource = String(request.source_store) === String(myStoreId);
    const isDestination = String(request.destination_store) === String(myStoreId);
    const nextForMe = new Set([
      ...(isSource ? SOURCE_TRANSITIONS[request.status] || [] : []),
      ...(isDestination ? DESTINATION_TRANSITIONS[request.status] || [] : []),
    ]);

    if (nextForMe.size === 0) return <span className="sxfer-muted">-</span>;

    return (
      <div className="sxfer-row-actions">
        {Array.from(nextForMe).map((status) =>
          status === "received" ? (
            <button key={status} type="button" className="sxfer-action-btn success" onClick={() => setReceiveTarget(request)}>
              <PackageCheck size={14} /> Mark Received
            </button>
          ) : (
            <button
              key={status}
              type="button"
              className={`sxfer-action-btn ${status === "rejected" || status === "cancelled" ? "danger" : ""}`}
              onClick={() => openAction(request, status)}
            >
              {STATUS_LABELS[status]}
            </button>
          ),
        )}
      </div>
    );
  };

  return (
    <div className="responsive-box sxfer-page">
      <ToastContainer />
      <div className="sxfer-header">
        <div>
          <h2>
            <ArrowLeftRight size={20} /> Stock Transfer
          </h2>
          <p>Request stock from another one of your stores, or review requests other stores have sent you.</p>
        </div>
      </div>

      <div className="sxfer-tabs">
        <button type="button" className={activeTab === "find" ? "active" : ""} onClick={() => setActiveTab("find")}>
          <PackageSearch size={15} /> Find Product in Other Store
        </button>
        <button type="button" className={activeTab === "requests" ? "active" : ""} onClick={() => setActiveTab("requests")}>
          <Truck size={15} /> Transfer Requests
        </button>
      </div>

      {activeTab === "find" && (
        <div className="sxfer-panel">
          {otherStores.length === 0 ? (
            <div className="sxfer-empty">
              You don't have any other associated stores to request stock from yet.
            </div>
          ) : (
            <>
              <div className="sxfer-toolbar">
                <div className="sxfer-search-box sxfer-search-box-wide">
                  <Search size={15} />
                  <input
                    type="text"
                    placeholder="Search product title or barcode across all your stores..."
                    value={browseSearch}
                    onChange={(event) => setBrowseSearch(event.target.value)}
                  />
                </div>
              </div>

              <div className="sxfer-table-wrap">
                <table className="sxfer-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Barcode</th>
                      <th>Category</th>
                      <th>Store</th>
                      <th>Available Stock</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {browseLoading ? (
                      <tr>
                        <td colSpan={6} className="sxfer-empty-cell">Searching all your stores...</td>
                      </tr>
                    ) : browseGroups.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="sxfer-empty-cell">No matching products with available stock found in any of your stores.</td>
                      </tr>
                    ) : (
                      browseGroups.map((group) => (
                        <tr key={`${group.storeId}-${group.productId}`}>
                          <td><strong>{group.title}</strong></td>
                          <td>{group.barcode || "-"}</td>
                          <td>{group.categories || "-"}</td>
                          <td>{group.storeName}</td>
                          <td className="sxfer-strong-green">{group.availableStock} pcs</td>
                          <td>
                            <button type="button" className="sxfer-action-btn" onClick={() => setRequestProduct(group)}>
                              Request Stock
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {activeTab === "requests" && (
        <div className="sxfer-panel">
          <div className="sxfer-subtabs">
            <button
              type="button"
              className={requestDirection === "incoming" ? "active" : ""}
              onClick={() => setRequestDirection("incoming")}
            >
              Incoming (asking me for stock)
            </button>
            <button
              type="button"
              className={requestDirection === "outgoing" ? "active" : ""}
              onClick={() => setRequestDirection("outgoing")}
            >
              Outgoing (my requests)
            </button>
          </div>

          <div className="sxfer-table-wrap">
            <table className="sxfer-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Qty</th>
                  <th>{requestDirection === "incoming" ? "Requested By" : "Requesting From"}</th>
                  <th>Status</th>
                  <th>Remarks</th>
                  <th>Requested</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {requestsLoading ? (
                  <tr>
                    <td colSpan={7} className="sxfer-empty-cell">Loading...</td>
                  </tr>
                ) : requests.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="sxfer-empty-cell">No transfer requests here yet.</td>
                  </tr>
                ) : (
                  requests.map((request) => (
                    <tr key={request.id}>
                      <td>
                        <strong>{request.product_title}</strong>
                        {request.product_barcode && (
                          <small className="sxfer-table-subtext">Barcode: {request.product_barcode}</small>
                        )}
                      </td>
                      <td>{request.requested_qty}</td>
                      <td>
                        {requestDirection === "incoming" ? request.destination_store_name : request.source_store_name}
                      </td>
                      <td>
                        <span className={`sxfer-chip ${STATUS_CHIP_CLASS[request.status] || ""}`}>
                          {STATUS_LABELS[request.status] || request.status}
                        </span>
                      </td>
                      <td className="sxfer-remarks-cell">{request.remarks || "-"}</td>
                      <td>{request.requested_at ? new Date(request.requested_at).toLocaleString() : "-"}</td>
                      <td>{renderActions(request)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <RequestQtyModal
        product={requestProduct}
        onCancel={() => setRequestProduct(null)}
        onConfirm={handleSendRequest}
        submitting={submitting}
      />

      <ActionModal
        action={pendingAction}
        onCancel={() => setPendingAction(null)}
        onConfirm={handleConfirmAction}
        submitting={submitting}
      />

      <ReceiveModal
        request={receiveTarget}
        onCancel={() => setReceiveTarget(null)}
        onConfirm={handleConfirmReceive}
        submitting={submitting}
      />

      <style>{`
        .sxfer-page { display: flex; flex-direction: column; gap: 16px; }
        .sxfer-header h2 { display: flex; align-items: center; gap: 8px; margin: 0 0 4px; font-size: 20px; }
        .sxfer-header p { margin: 0; color: #64748b; font-size: 13px; }

        .sxfer-tabs { display: flex; gap: 8px; border-bottom: 1px solid #e2e8f0; }
        .sxfer-tabs button {
          display: flex; align-items: center; gap: 6px;
          padding: 10px 16px; border: none; background: none; cursor: pointer;
          font-weight: 600; font-size: 13px; color: #64748b;
          border-bottom: 2px solid transparent;
        }
        .sxfer-tabs button.active { color: #282e26; border-bottom-color: #282e26; }

        .sxfer-panel { display: flex; flex-direction: column; gap: 14px; }
        .sxfer-empty, .sxfer-empty-cell { padding: 24px; text-align: center; color: #94a3b8; font-size: 13px; }

        .sxfer-toolbar { display: flex; gap: 12px; flex-wrap: wrap; }
        .sxfer-toolbar select {
          height: 40px; border-radius: 8px; border: 1px solid #d1d5db; padding: 0 10px; min-width: 220px;
        }
        .sxfer-search-box {
          flex: 1; min-width: 220px; display: flex; align-items: center; gap: 8px;
          border: 1px solid #d1d5db; border-radius: 8px; padding: 0 10px; height: 40px;
        }
        .sxfer-search-box input { border: none; outline: none; flex: 1; font-size: 13px; }
        .sxfer-search-box-wide { max-width: 480px; }

        .sxfer-subtabs { display: flex; gap: 8px; }
        .sxfer-subtabs button {
          padding: 8px 14px; border-radius: 999px; border: 1px solid #d1d5db; background: #ffffff;
          cursor: pointer; font-size: 12px; font-weight: 600; color: #475569;
        }
        .sxfer-subtabs button.active { background: #282e26; color: #ffffff; border-color: #282e26; }

        .sxfer-table-wrap { overflow-x: auto; border: 1px solid #e2e8f0; border-radius: 10px; }
        .sxfer-table { width: 100%; border-collapse: collapse; font-size: 13px; }
        .sxfer-table th {
          text-align: left; padding: 10px 12px; background: #f8fafc; color: #64748b;
          font-size: 11px; text-transform: uppercase; letter-spacing: 0.03em; white-space: nowrap;
        }
        .sxfer-table td { padding: 10px 12px; border-top: 1px solid #f1f5f9; vertical-align: top; }
        .sxfer-table-subtext { display: block; color: #94a3b8; font-size: 11px; }
        .sxfer-strong-green { color: #16a34a; font-weight: 700; }
        .sxfer-remarks-cell { max-width: 220px; white-space: normal; color: #64748b; }
        .sxfer-muted { color: #94a3b8; }

        .sxfer-chip {
          display: inline-flex; align-items: center; padding: 3px 10px; border-radius: 999px;
          font-size: 11px; font-weight: 800; white-space: nowrap;
        }
        .sxfer-chip-info { background: #eff6ff; color: #1d4ed8; border: 1px solid #bfdbfe; }
        .sxfer-chip-warning { background: #fffbeb; color: #b45309; border: 1px solid #fde68a; }
        .sxfer-chip-success { background: #ecfdf5; color: #047857; border: 1px solid #a7f3d0; }
        .sxfer-chip-danger { background: #fef2f2; color: #b91c1c; border: 1px solid #fecaca; }

        .sxfer-row-actions { display: flex; gap: 6px; flex-wrap: wrap; }
        .sxfer-action-btn {
          display: inline-flex; align-items: center; gap: 5px;
          border: 1px solid #d1d5db; background: #ffffff; border-radius: 6px;
          padding: 5px 10px; font-size: 11px; font-weight: 700; cursor: pointer; color: #282e26;
          white-space: nowrap;
        }
        .sxfer-action-btn:hover { background: #f3f4f6; }
        .sxfer-action-btn.danger { color: #b91c1c; border-color: #fecaca; }
        .sxfer-action-btn.success { color: #047857; border-color: #a7f3d0; }

        .sxfer-action-modal p { font-size: 13px; color: #334155; margin: 0 0 12px; }
        .sxfer-form-group { display: flex; flex-direction: column; gap: 6px; margin-bottom: 12px; }
        .sxfer-form-group label { font-size: 12px; font-weight: 700; color: #475569; }
        .sxfer-form-group textarea, .sxfer-form-group input {
          border: 1px solid #d1d5db; border-radius: 8px; padding: 8px 10px; font-size: 13px; font-family: inherit;
        }
        .sxfer-hint { font-size: 11px; color: #94a3b8; }
        .sxfer-linktoggle {
          background: none; border: none; padding: 0; margin-top: 4px;
          color: #2563eb; font-size: 12px; font-weight: 700; cursor: pointer; text-align: left;
        }
        .sxfer-linktoggle:hover { text-decoration: underline; }
        .sxfer-hint-warning { color: #b45309; }
        .sxfer-modal-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 6px; }
      `}</style>
    </div>
  );
};

export default StockTransfer;
